/**
 * Compact internal-link summary for the editor sidebar.
 *
 * Shows for the article being edited:
 *   - inbound count (# of other factory articles linking TO this one)
 *   - outbound count (# of factory cross-links this article has)
 *   - hub count (# of hand-built hub-page links)
 *   - orphan badge if inbound = 0
 *
 * Expand to see the actual inbound + outbound lists with click-through.
 * Backed by GET /api/sites/:siteId/links/:slug.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../lib/api.ts'

interface LinkDetail {
  slug: string
  title: string
  outbound: string[]
  inboundCount: number
  hubLinks: string[]
  isOrphan: boolean
  isHubOnly: boolean
  inbound: { slug: string; title: string }[]
}

interface Props {
  siteId: string
  slug: string
}

export function LinksSummary({ siteId, slug }: Props) {
  const [data, setData] = useState<LinkDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    api<LinkDetail>(`/api/sites/${siteId}/links/${slug}`)
      .then((res) => !cancelled && setData(res))
      .catch((e) => !cancelled && setErr((e as Error).message))
    return () => {
      cancelled = true
    }
  }, [siteId, slug])

  if (err || !data) return null

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '0.6rem 0.75rem',
        background: 'var(--bg-muted)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        fontSize: '0.75rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(!open)}
      >
        <strong style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-faint)' }}>
          Internal links
        </strong>
        <span style={{ color: 'var(--text-faint)' }}>{open ? '▾' : '▸'}</span>
      </div>
      <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
        <Stat
          label="inbound"
          value={data.inboundCount}
          danger={data.isOrphan}
          title={data.isOrphan ? 'Orphan: no factory article links to this one' : `${data.inboundCount} factory article(s) link here`}
        />
        <Stat
          label="outbound"
          value={data.outbound.length}
          title={`${data.outbound.length} cross-factory link(s)`}
        />
        <Stat label="hub" value={data.hubLinks.length} title={`${data.hubLinks.length} hand-built hub-page link(s)`} />
      </div>

      {open && (
        <div style={{ marginTop: '0.6rem' }}>
          <ExpandList
            title={`↓ Inbound (${data.inbound.length})`}
            empty="No factory article links here yet."
            items={data.inbound.map((i) => ({
              key: i.slug,
              label: i.title,
              to: `/sites/${siteId}/articles/${i.slug}`,
            }))}
          />
          <ExpandList
            title={`↑ Outbound (${data.outbound.length})`}
            empty="No factory cross-links in this article."
            items={data.outbound.map((s) => ({
              key: s,
              label: s.replace(/-/g, ' '),
              to: `/sites/${siteId}/articles/${s}`,
            }))}
          />
        </div>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  title,
  danger = false,
}: {
  label: string
  value: number
  title?: string
  danger?: boolean
}) {
  return (
    <span
      title={title}
      style={{
        padding: '0.15rem 0.45rem',
        borderRadius: 'var(--radius)',
        background: danger ? '#fef2f2' : 'var(--bg-elev)',
        color: danger ? 'var(--danger)' : 'var(--text)',
        fontSize: '0.72rem',
      }}
    >
      <strong>{value}</strong> {label}
    </span>
  )
}

function ExpandList({
  title,
  empty,
  items,
}: {
  title: string
  empty: string
  items: { key: string; label: string; to: string }[]
}) {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div style={{ color: 'var(--text-faint)', fontSize: '0.7rem', marginBottom: '0.25rem' }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ color: 'var(--text-faint)', fontStyle: 'italic', fontSize: '0.7rem' }}>{empty}</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.slice(0, 8).map((it) => (
            <li key={it.key} style={{ padding: '0.15rem 0', borderTop: '1px solid var(--border)' }}>
              <Link to={it.to} style={{ fontSize: '0.72rem' }}>
                {it.label}
              </Link>
            </li>
          ))}
          {items.length > 8 && (
            <li style={{ color: 'var(--text-faint)', fontSize: '0.7rem', paddingTop: '0.2rem' }}>
              + {items.length - 8} more
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
